/* sp-variant.js — <sp-variant-picker> (snippets/sp-variant-picker.liquid) on the v2 product section.
   Option inputs → variant (embedded #sp-variant-map-<section>) → hidden [name=id] in #product-form-<section>
   (+ `change` for third-party buy-button / COD apps, ?variant= on product pages only) → `sp:variant` on the
   section root (sp-bundle.js re-bases tier + sticky prices and toggles the buy buttons) → `sp:gallery:goto`
   (variant image). Bundle tier cards are never touched here. No UI text: the "unavailable" template comes from
   data-t-unavailable (Shopify's own products.product.value_unavailable string, rendered by Liquid). */
(function () {
  'use strict';

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  function money(cents) {
    var n = Number(cents) || 0;
    if (window.sp && typeof window.sp.money === 'function') return window.sp.money(n);
    return (n / 100).toFixed(2);
  }

  // Sentinel fill: "[value]" → option value (textContent only, never innerHTML).
  function fill(template, key, value) {
    return String(template == null ? '' : template).split('[' + key + ']').join(String(value));
  }

  function within(el, selector) {
    return !!(el && typeof el.closest === 'function' && el.closest(selector));
  }

  class SpVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.spReady) return;
      this.sectionId = this.dataset.section || '';
      this.root = document.getElementById('MainProduct-' + this.sectionId) || this.closest('[data-section]') || null;
      this.variants = readJSON('sp-variant-map-' + this.sectionId);
      if (!this.root || !Array.isArray(this.variants) || !this.variants.length) return;
      this.fieldsets = Array.prototype.slice.call(this.querySelectorAll('[data-sp-option-fieldset]'));
      if (!this.fieldsets.length) return;
      this.spReady = true;
      this.updateUrl = this.dataset.context === 'page';
      this.unavailableTpl = this.dataset.tUnavailable || '[value]';

      // Only our own option controls. The buy form's hidden [name=id] fires `change` too (sp-bundle.js
      // dispatches it for buy-button apps) — it lives outside this element and carries no data-sp-option, so it
      // can never re-enter here (no sp:variant loop).
      this.onChange = (e) => {
        var t = e.target;
        if (!t || !this.contains(t) || typeof t.hasAttribute !== 'function' || !t.hasAttribute('data-sp-option')) return;
        this.apply();
      };
      this.addEventListener('change', this.onChange);

      // Initial state is server-rendered (selected_or_first_available_variant). Only re-sync when a restored
      // form state (bfcache / back navigation) disagrees with the hidden id.
      var variant = this.resolve();
      var idInput = this.idInput();
      if (variant && idInput && String(idInput.value) !== String(variant.id)) this.apply();
      else this.render(variant);
    }

    disconnectedCallback() {
      if (this.onChange) this.removeEventListener('change', this.onChange);
    }

    /* ---------- lookups ---------- */

    form() {
      return document.getElementById('product-form-' + this.sectionId) || this.root.querySelector('form[data-type="add-to-cart-form"]');
    }

    idInput() {
      var form = this.form();
      return form ? form.querySelector('[name="id"]') : null;
    }

    // Selected option VALUE strings in fieldset (= option) order — the order of variant.options.
    selectedValues() {
      return this.fieldsets.map(function (fs) {
        var select = fs.querySelector('select[data-sp-option]');
        if (select) return select.value;
        var checked = fs.querySelector('input[data-sp-option]:checked');
        return checked ? checked.value : null;
      });
    }

    match(values) {
      return (
        this.variants.find(function (v) {
          return (
            v &&
            Array.isArray(v.options) &&
            v.options.length === values.length &&
            v.options.every(function (o, i) {
              return o === values[i];
            })
          );
        }) || null
      );
    }

    resolve() {
      return this.match(this.selectedValues());
    }

    // Dawn's rule: with the OTHER options as currently selected, a value is available when at least one
    // available variant carries it.
    isValueAvailable(index, value, selected) {
      return this.variants.some(function (v) {
        if (!v || v.available === false || !Array.isArray(v.options) || v.options[index] !== value) return false;
        for (var j = 0; j < selected.length; j++) {
          if (j !== index && selected[j] != null && v.options[j] !== selected[j]) return false;
        }
        return true;
      });
    }

    /* ---------- rendering ---------- */

    render(variant) {
      var selected = this.selectedValues();
      this.fieldsets.forEach((fs, i) => {
        var legendValue = fs.querySelector('[data-sp-option-value]');
        if (legendValue && selected[i] != null) legendValue.textContent = selected[i];
        Array.prototype.forEach.call(fs.querySelectorAll('input[data-sp-option]'), (input) => {
          var ok = this.isValueAvailable(i, input.value, selected);
          if (ok) input.removeAttribute('data-unavailable');
          else input.setAttribute('data-unavailable', '');
          var pill = input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL' ? input.nextElementSibling : null;
          if (!pill) return;
          pill.classList.toggle('is-unavailable', !ok);
          var sr = pill.querySelector('[data-sp-unavailable-text]');
          if (sr) sr.hidden = ok;
        });
        var select = fs.querySelector('select[data-sp-option]');
        if (select) {
          Array.prototype.forEach.call(select.options, (opt) => {
            var ok = this.isValueAvailable(i, opt.value, selected);
            if (ok) opt.removeAttribute('data-unavailable');
            else opt.setAttribute('data-unavailable', '');
            opt.textContent = ok ? opt.value : fill(this.unavailableTpl, 'value', opt.value);
          });
        }
      });
      this.classList.toggle('is-unavailable', !variant || variant.available === false);
    }

    // Plain (non-bundle) price hooks, if the section ever renders them: [data-sp-price] / [data-sp-compare]
    // outside the bundle cards and the sticky bar (both are written by sp-bundle.js on sp:variant).
    updatePriceHooks(variant) {
      var skip = '[data-sp-bundle], [data-sp-sticky-atc], sp-variant-picker';
      var hasCompare = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;
      Array.prototype.forEach.call(this.root.querySelectorAll('[data-sp-price]'), function (el) {
        if (!within(el, skip)) el.textContent = money(variant.price);
      });
      Array.prototype.forEach.call(this.root.querySelectorAll('[data-sp-compare]'), function (el) {
        if (within(el, skip)) return;
        el.textContent = hasCompare ? money(variant.compare_at_price) : '';
        el.hidden = !hasCompare;
      });
    }

    /* ---------- selection ---------- */

    // Standard buy-button contract (C1): third-party apps only see a selection through a `change` on the hidden
    // [name=id] and the ?variant= URL param. A bare `.value =` write is invisible to both. URL sync is a
    // product-page (data-context="page") behaviour only.
    syncFormSignals(variantId) {
      var idInput = this.idInput();
      if (idInput) {
        idInput.value = String(variantId);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (!this.updateUrl) return;
      try {
        var params = new URLSearchParams(window.location.search);
        params.set('variant', String(variantId));
        window.history.replaceState({}, '', window.location.pathname + '?' + params.toString() + window.location.hash);
      } catch (e) {
        // URL sync is best-effort — never break the PDP over it.
      }
    }

    apply() {
      var variant = this.resolve();
      this.render(variant);
      if (!variant) {
        // Combination without a variant (non-cartesian products): keep the last valid hidden id, block the buy
        // buttons. The next valid selection re-enables them through sp-bundle.js (sp:variant → available).
        [this.root.querySelector('[data-sp-atc]'), this.root.querySelector('[data-sp-sticky-button]')].forEach(function (btn) {
          if (btn) btn.setAttribute('disabled', '');
        });
        return;
      }
      this.syncFormSignals(variant.id);
      this.updatePriceHooks(variant);
      this.root.dispatchEvent(
        new CustomEvent('sp:variant', {
          bubbles: true,
          detail: {
            sectionId: this.sectionId,
            source: 'sp-variant-picker',
            variant: {
              id: variant.id,
              price: variant.price,
              compare_at_price: variant.compare_at_price,
              available: variant.available,
              featured_media_id: variant.featured_media_id
            }
          }
        })
      );
      if (variant.featured_media_id) {
        var gallery = this.root.querySelector('sp-gallery');
        (gallery || this.root).dispatchEvent(new CustomEvent('sp:gallery:goto', { bubbles: false, detail: { mediaId: variant.featured_media_id } }));
      }
    }
  }

  if (!customElements.get('sp-variant-picker')) customElements.define('sp-variant-picker', SpVariantPicker);
})();
